/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Picking a legible ink for a background chosen at runtime.
 *
 * A component that accepts a background colour as a prop and lets its text
 * inherit whatever ink is around it is a contrast bug waiting for the wrong
 * input: the app header painted the user avatar with `--seed-base` (#4a0e11)
 * and the initial kept `--text-primary` (#1a1a1a), which measures 1.13:1 —
 * the letter was there, and invisible. Whoever paints the background has to
 * decide the ink.
 *
 * These helpers work on *resolved* colours, i.e. what `getComputedStyle`
 * returns (`rgb(...)`/`rgba(...)`), so a caller can pass `var(--seed-base)`
 * to the DOM and derive the ink from what the browser actually computed. No
 * CSS function can do this: `color-contrast()` is still unshipped.
 */

/** Body ink, the darkest candidate. Matches `--text-primary`. */
export const DARK_INK = '#1a1a1a';

/** Inverse ink, the lightest candidate. Matches `--text-inverse`. */
export const LIGHT_INK = '#ffffff';

/** WCAG AA minimum for normal-size text. */
export const MIN_TEXT_CONTRAST = 4.5;

type Rgb = [number, number, number];

const DARK_RGB: Rgb = [26, 26, 26];
const LIGHT_RGB: Rgb = [255, 255, 255];

/**
 * Reads the numeric channels out of a resolved CSS colour.
 *
 * Accepts the `rgb(r, g, b)` / `rgba(r, g, b, a)` and `rgb(r g b / a)` forms
 * a computed style returns, plus `#rrggbb` for callers that hold a literal.
 * Anything else — an unresolved `var(...)`, a named colour, `oklch(...)` —
 * returns null, and the caller is expected to leave the ink alone rather
 * than guess.
 *
 * @param color - A CSS colour string.
 * @returns The sRGB channels (0-255), or null when unparseable.
 */
export function parseCssColor(color: string | undefined): Rgb | null {
  if (!color) return null;
  return parseHexColor(color.trim()) ?? parseRgbColor(color.trim());
}

/**
 * The `#rrggbb` form, unpacked a byte at a time.
 */
function parseHexColor(color: string): Rgb | null {
  const hex = /^#([0-9a-f]{6})$/i.exec(color);
  if (!hex) return null;
  const value = parseInt(hex[1], 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

/**
 * The `rgb()`/`rgba()` form; alpha is read and discarded.
 */
function parseRgbColor(color: string): Rgb | null {
  const channels = /^rgba?\(([^)]+)\)$/i.exec(color);
  if (!channels) return null;
  const parts = channels[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  if (!hasThreeNumbers(parts)) return null;
  return [parts[0], parts[1], parts[2]];
}

/**
 * Whether the first three parsed channels are all real numbers.
 */
function hasThreeNumbers(parts: number[]): boolean {
  return parts.length >= 3 && !parts.slice(0, 3).some(Number.isNaN);
}

/**
 * Linearises one sRGB channel, per the WCAG relative luminance formula.
 */
function linearize(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * WCAG relative luminance of an sRGB triplet.
 */
function relativeLuminance([r, g, b]: Rgb): number {
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * WCAG contrast ratio between two sRGB triplets.
 *
 * @param a - The first colour.
 * @param b - The second colour.
 * @returns The ratio, from 1 (identical) to 21 (black on white).
 */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const lumA = relativeLuminance(a);
  const lumB = relativeLuminance(b);
  return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
}

/**
 * The more legible of the two app inks against a resolved background.
 *
 * Deliberately only two candidates: the app's own body and inverse inks, so
 * a derived avatar or badge still looks like the rest of the product. Both
 * clear AA for every colour in the seed palette; a mid-tone grey around
 * #767676 is the worst case either can reach (~4.5:1), which is why
 * `meetsTextContrast` exists for callers that need to know rather than
 * assume.
 *
 * @param background - The background colour, resolved (see parseCssColor).
 * @returns The ink to paint text with, or null when `background` is unparseable.
 */
export function readableInkOn(background: string | undefined): string | null {
  const rgb = parseCssColor(background);
  if (!rgb) return null;
  return contrastRatio(rgb, LIGHT_RGB) >= contrastRatio(rgb, DARK_RGB) ? LIGHT_INK : DARK_INK;
}

/**
 * Whether an ink/background pair clears WCAG AA for normal-size text.
 *
 * @param ink - The text colour, resolved.
 * @param background - The background colour, resolved.
 * @returns True when the pair reaches 4.5:1; false when it doesn't or either
 *          colour is unparseable.
 */
export function meetsTextContrast(ink: string | undefined, background: string | undefined): boolean {
  const inkRgb = parseCssColor(ink);
  const bgRgb = parseCssColor(background);
  if (!inkRgb || !bgRgb) return false;
  return contrastRatio(inkRgb, bgRgb) >= MIN_TEXT_CONTRAST;
}
